import type React from "react"

import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Textarea } from "./ui/textarea"
import { Phone, Mail, MapPin, Send } from "lucide-react"

interface ContactSectionProps {
  t: Record<string, any>
}

export function ContactSection({ t }: ContactSectionProps) {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Simulate form submission
    setTimeout(() => {
      setSubmitted(true)
      setFormData({ name: "", email: "", message: "" })
      setLoading(false)
      setTimeout(() => setSubmitted(false), 3000)
    }, 1000)
  }

  const contactInfo = [
    {
      icon: Phone,
      label: t?.contact?.phone || "Phone",
      value: "+998 (90) 97-377-33-66",
      href: "tel:+998973773366",
    },
    {
      icon: Mail,
      label: t?.contact?.email || "Email",
      value: "support@cardin.uz",
      href: "mailto:support@cardin.uz",
    },
    {
      icon: MapPin,
      label: t?.contact?.address || "Address",
      value: "Samarkand , Uzbekistan",
      href: "#",
    },
  ]

  return (
    <section id="contact" className="py-20 px-4 bg-black">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">{t?.contact?.title || "Get In Touch"}</h2>
          <p className="text-gray-400">We'd love to hear from you. Send us a message or contact us directly!</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon
              return (
                <motion.a
                  key={index}
                  href={info.href}
                  whileHover={{ x: 4 }}
                  className="flex items-start gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                >
                  <Icon className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-sm text-gray-400">{info.label}</p>
                    <p className="text-white font-medium">{info.value}</p>
                  </div>
                </motion.a>
              )
            })}

            {/* Telegram Link */}
            <motion.a
              href="https://t.me/cardintaxi"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ x: 4 }}
              className="flex items-start gap-4 p-4 rounded-lg bg-gradient-to-r from-blue-600/10 to-blue-400/10 border border-blue-500/20 hover:border-blue-500/40 transition-all"
            >
              <Send className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400">Telegram</p>
                <p className="text-white font-medium">@CardinTaxi</p>
              </div>
            </motion.a>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder={t?.contact?.form?.name || "Your Name"}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                required
              />
              <Input
                type="email"
                placeholder={t?.contact?.form?.email || "Your Email"}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                required
              />
              <Textarea
                placeholder={t?.contact?.form?.message || "Your Message"}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-32"
                required
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold"
              >
                {submitted
                  ? t?.contact?.form?.success || "Message Sent!"
                  : loading
                    ? "Sending..."
                    : t?.contact?.form?.send || "Send Message"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
